import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/lib/lang";
import { scrollToTarget } from "@/lib/lenis";
import { ZMark } from "@/components/ZMark";
import { EASE } from "@/components/motion";

export const NavAnchor = ({ id, children, className = "", testid, onClick }) => {
  const { pathname } = useLocation();
  return (
    <Link
      to={`/#${id}`}
      data-testid={testid}
      className={className}
      onClick={(e) => {
        onClick?.();
        if (pathname === "/") {
          e.preventDefault();
          scrollToTarget(`#${id}`);
        }
      }}
    >
      {children}
    </Link>
  );
};

export const LangSwitch = ({ prefix = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="relative flex rounded-full border border-white/10 bg-white/[0.03] p-1 font-mono text-[11px]" data-testid={`${prefix}lang-switch`}>
      {["en", "da"].map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`${prefix}lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`relative rounded-full px-2.5 py-1 uppercase tracking-[0.2em] transition-colors duration-300 ${lang === l ? "text-[#05070f]" : "text-white/60 hover:text-white"}`}
        >
          {lang === l && (
            <motion.span layoutId={`${prefix}lang-pill`} className="absolute inset-0 rounded-full bg-gradient-to-r from-[#22d3ee] to-[#5cf2a0]" transition={{ type: "spring", stiffness: 420, damping: 32 }} />
          )}
          <span className="relative">{l}</span>
        </button>
      ))}
    </div>
  );
};

const linkCls = "nav-link relative px-4 py-2 text-sm text-white/70 transition-colors duration-300 hover:text-white";

export const Nav = () => {
  const { t } = useLang();
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(v > prev && v > 240);
  });
  const links = [["apps", t.nav.apps], ["about", t.nav.about], ["contact", t.nav.contact]];

  return (
    <motion.header
      data-testid="site-nav"
      className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-6 sm:pt-5"
      animate={{ y: hidden && !open ? -110 : 0 }}
      transition={{ duration: 0.6, ease: EASE }}
    >
      <nav className="mx-auto flex max-w-[1500px] items-center justify-between rounded-full border border-white/10 bg-[#070a14]/65 py-2 pl-4 pr-2 backdrop-blur-xl">
        <Link to="/" data-testid="nav-brand-logo" className="group flex items-center gap-2.5">
          <ZMark id="nav-z" className="h-8 w-8 transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)] group-hover:rotate-180" />
          <span className="font-display text-lg font-bold tracking-tight">ZenVio</span>
        </Link>
        <div className="hidden items-center md:flex">
          {links.map(([id, label]) => (
            <NavAnchor key={id} id={id} testid={`nav-link-${id}`} className={linkCls}>{label}</NavAnchor>
          ))}
          <Link to="/privacy" data-testid="nav-link-privacy" className={linkCls}>{t.nav.privacy}</Link>
        </div>
        <div className="flex items-center gap-2">
          <LangSwitch />
          <NavAnchor id="contact" testid="nav-cta-button" className="btn-primary hidden !py-2.5 sm:inline-flex">{t.nav.cta}</NavAnchor>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label={open ? t.nav.close : t.nav.menu}
            onClick={() => setOpen((o) => !o)}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/10 md:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="mx-auto mt-2 max-w-[1500px] rounded-[28px] border border-white/10 bg-[#070a14]/90 p-6 backdrop-blur-xl md:hidden"
          >
            {links.map(([id, label], i) => (
              <NavAnchor key={id} id={id} testid={`mobile-nav-link-${id}`} onClick={() => setOpen(false)} className="flex items-baseline gap-4 border-b border-white/10 py-4 font-display text-3xl font-bold tracking-tight">
                <span className="font-mono text-xs text-white/40">0{i + 1}</span>{label}
              </NavAnchor>
            ))}
            <Link to="/privacy" onClick={() => setOpen(false)} data-testid="mobile-nav-link-privacy" className="flex items-baseline gap-4 py-4 font-display text-3xl font-bold tracking-tight">
              <span className="font-mono text-xs text-white/40">04</span>{t.nav.privacy}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
